import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { useZed } from '@modules/core/src/react/useZed';
import { Layers } from '@modules/core/src/utils/Layers';
import { onTouchEndObservable } from '@modules/standard/src/observable/CommonObservable';
import { StandardModule } from '@modules/standard/src/StandardModule';
import zams from '@zams/logger/zed';
import { BottomSheet, ListItem } from '@zigbang/zuix2';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useScreen } from '@modules/tourdanji/src/react/hooks/useScreen';

import { DongGuiResponse } from '../ecs/component/response/Dong';
import { UnitResponse } from '../ecs/component/response/Unit';
import { getTourDanjiMetaData, getTourDanjiZamsParams, TourDanjiModule } from '../TourDanjiModule';
import { sortUnitNames } from '../utiles/Utils';

import { Dialog } from './Dialog';
import { EnterDongTooltip } from './EnterDongTooltip';
import { getItemStyle, GUIView, preUpdateGUI } from './GUIUtiles';
import style from './styles/DongPanel.module.css';

export function DongPanel() {
  const zed = useZed<[BabylonjsModule, StandardModule, TourDanjiModule]>();
  const screenInfo = useScreen();
  const [dongGUIs, setDongGUIs] = useState<DongGuiResponse[]>([]);
  const [isNearCamera, setIsNearCamera] = useState(true);
  const [dongPanelCSS, setDongPanelCSS] = useState(style.dongPanel);
  const [simpleDongPanelCSS, setSimpleDongPanelCSS] = useState(style.simpleDongPanel);
  const [selectedDong, setSelectedDong] = useState<DongGuiResponse | null>(null);
  const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false);
  const [units, setUnits] = useState<UnitResponse[]>([]);
  const [nearestDongId, setNearestDongId] = useState<number | null>(null);
  const [isUnitViewActive, setIsUnitViewActive] = useState(false);
  const dongGUIsRef = useRef(dongGUIs);
  const isFirstLoadRef = useRef(true);
  const { isDesktop } = screenInfo;

  useEffect(() => {
    if (screenInfo.isInComponent) {
      setDongPanelCSS(`${style.dongPanel} ${style.hide}`);
      setSimpleDongPanelCSS(`${style.simpleDongPanel} ${style.show}`);
      setTimeout(() => {
        setDongPanelCSS(`${style.dongPanel} ${style.hideEnd}`);
        setSimpleDongPanelCSS(`${style.simpleDongPanel}`);
      }, 300);
    } else {
      if (isNearCamera) {
        setDongPanelCSS(`${style.dongPanel} ${style.show}`);
        setSimpleDongPanelCSS(`${style.simpleDongPanel} ${style.hide}`);
        setTimeout(() => {
          setSimpleDongPanelCSS(`${style.simpleDongPanel} ${style.hideEnd}`);
          setDongPanelCSS(style.dongPanel);
        }, 300);
      } else {
        setDongPanelCSS(`${style.dongPanel} ${style.hide}`);
        setSimpleDongPanelCSS(`${style.simpleDongPanel} ${style.show}`);
        setTimeout(() => {
          setDongPanelCSS(`${style.dongPanel} ${style.hideEnd}`);
          setSimpleDongPanelCSS(`${style.simpleDongPanel}`);
        }, 300);
      }
    }
  }, [isNearCamera]);

  useEffect(() => {
    if (!zed) return;

    zed.api.observer.onDongLoaded.addOnce(() => {
      zed.api.observer.onUpdateDongGUI.add((data) => {
        setDongGUIs(JSON.parse(JSON.stringify(data)));
        dongGUIsRef.current = data;
        
        data.map((item, index) => {
          preUpdateGUI(document.getElementById(`${item.object.dongName}${index}`), {
            ...item,
            iconScaleOffset: 0,
          });
        });
        
        // 처음 로딩 시에만 가장 가까운 동을 찾아 툴팁 표시
        if (isFirstLoadRef.current && data.length > 0) {
          isFirstLoadRef.current = false;
          const nearest = findNearestDongToCenter(data);
          if (nearest) {
            setNearestDongId(nearest.object.dongId);
          }
        }
      });
    });

    const touchEndObserver = onTouchEndObservable.add(() => {      
      if (dongGUIsRef.current.length > 0) {
        const nearest = findNearestDongToCenter(dongGUIsRef.current);
        if (nearest) {
          setNearestDongId(nearest.object.dongId);
        }
      }
    });

    return () => {
      onTouchEndObservable.remove(touchEndObserver);
    };
  }, [zed]);

  useEffect(() => {
    const current = dongGUIs.some((item) => item.iconScaleOffset > 0.2);
    if (current !== isNearCamera) {
      setIsNearCamera(current);
    }
  }, [dongGUIs]);

  useEffect(() => {
    dongGUIsRef.current = dongGUIs;
  }, [dongGUIs]);

  useEffect(() => {
    if (isBottomSheetVisible) {
      zams.z153({
        ...getTourDanjiZamsParams(),
        zed_dong: selectedDong?.object.dongName ?? '',
      }).send();
    }
  }, [isBottomSheetVisible, selectedDong]);

  useEffect(() => {
    if (!zed) return;

    // Subscribe to unit view state changes
    const unitViewStateObserver = zed.api.observer.onUnitViewStateChanged.add(({ isEntering }) => {
      setIsUnitViewActive(isEntering);
    });

    return () => {
      zed.api.observer.onUnitViewStateChanged.remove(unitViewStateObserver);
    };
  }, [zed]);

  const handleDongClick = (dong: DongGuiResponse) => {
    // NOTE: 컴포넌트 뷰에서는 실내진입 불가
    if (screenInfo.isInComponent || getTourDanjiMetaData().disableInteriorView) {
      return;
    }
    
    setNearestDongId(null);
    

    zams.z152({
      ...getTourDanjiZamsParams(),
      zed_dong: dong?.object.dongName ?? '',
    }).send();

    const rawUnits = zed?.api.getUnitsInDong(dong.object.dongId);
    if (rawUnits) {
      const convertedUnits = rawUnits.map(unit => ({
        buildingName: dong.object.dongName,
        unitName: unit.hoName,
        unitMetadata: {
          ho_id: unit.hoId,
          호: unit.hoName,
          동: dong.object.dongName,
          룸타입id: 0,
          향: 0,
          높이: 0,
          층갯수: 0
        },
        unitDetail: {
          dongId: dong.object.dongId,
          dong: dong.object.dongName,
          hoId: unit.hoId,
          ho: unit.hoName
        } as any,
        floorIndex: 0
      }));
      setUnits(convertedUnits);
      setIsBottomSheetVisible(true);    
    }
    zed?.api.selectDong(dong.object.dongId);
    setSelectedDong(dong);
  };
  
  const handleUnitClick = (unit: UnitResponse) => {
    zams.z155({
      ...getTourDanjiZamsParams(),
      zed_dong: selectedDong?.object.dongName ?? '',
      zed_ho: unit.unitDetail.ho ?? ''
    }).send();

    if (selectedDong?.object.dongName && unit.unitDetail.ho) {
      zed?.api.enterUnit(selectedDong.object.dongName, unit.unitDetail.ho);
    }
    handleCloseBottomSheet();
  };

  const handleCloseBottomSheet = () => {
    setIsBottomSheetVisible(false);
    zed?.api.unselectDong();
    
    // 바텀 시트 애니메이션이 완료된 후에 selectedDong을 null로 설정
    setTimeout(() => {
      setSelectedDong(null);
    }, 300); // 바텀 시트 애니메이션 시간에 맞게 조정 (일반적으로 300ms)
  };

  const isClickable = useMemo(() => {
    if (getTourDanjiMetaData().disableInteriorView) {
      return false;
    }
    
    // NOTE: 컴포넌트 뷰에서는 실내진입 불가
    if (screenInfo.isInComponent) {
      return false;
    }

    return true;
  }, [screenInfo.isInComponent, getTourDanjiMetaData().disableInteriorView]);

  // 화면 중앙에 가장 가까운 동패널 찾기
  const findNearestDongToCenter = (guis: DongGuiResponse[] = dongGUIs): DongGuiResponse | null => {
    if (!guis.length) return null;
    
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    
    let nearestDong: DongGuiResponse | null = null;
    let minDistance = Number.MAX_VALUE;
    
    for (const dong of guis) {
      if (!dong.isVisible) continue;
      
      const distance = Math.sqrt(
        Math.pow(dong.x - centerX, 2) + Math.pow(dong.y - centerY, 2)
      );
      
      if (distance < minDistance) {
        minDistance = distance;
        nearestDong = dong;
      }
    }
    
    return nearestDong;
  };

  function renderDongItem(item: DongGuiResponse, index: number) {
    if (!item.isVisible) return null;
    
    return (
      <div
        key={item.key}
        id={`${item.object.dongName}${index}`}
        onClick={() => handleDongClick(item)}
        style={{
          ...getItemStyle({
            x: item.x,
            y: item.y,
            scaleWithCameraDistance: 0,
            zIndex: item.zIndex,
          }),
          pointerEvents: 'auto',
        }}
      >
        {((nearestDongId === item.object.dongId) && isClickable) && (
          <EnterDongTooltip />
        )}
        <div className={style.container}>
          <div 
            className={`${dongPanelCSS} ${selectedDong?.object.dongId === item.object.dongId ? style.selected : ''}`}            
          >
            <span className={style.dongTitle}>
              {item.object.dongName}
            </span>            
            <div className={style.dongFloorText}>{item.object.dongFloor}</div>
          </div>
          <div className={simpleDongPanelCSS}>
            <div className={style.simpleDongTitle}>
              <span className={style.dongNumber}>{item.object.dongName}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isUnitViewActive) {
    return null;
  }

  return (
    <>
      <div style={{ display: 'block'}}>
        <GUIView>
          {dongGUIs.map((item, index) => renderDongItem(item, index))}
        </GUIView>
      </div>
      <div style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: Layers.UI_MODAL,
        pointerEvents: 'none',
        display: isUnitViewActive ? 'none' : 'block'
      }}>
        <div style={{ pointerEvents: 'auto' }}>
          {isDesktop ? (
            <Dialog        
              visible={isBottomSheetVisible}
              onClose={() => {
                handleCloseBottomSheet();                
              }}
              title={`${selectedDong?.object.dongName} 세대 확인`}
            >
              <div className={`${style.desktopScrollable}`} style={{ 
                width: '300px',
                maxHeight: '326px',
                overflowY: 'auto',
                touchAction: 'pan-y',
                display: 'flex',
                flexDirection: 'column',
              }}>
                {units.sort((a, b) => sortUnitNames(a.unitName, b.unitName)).map((unit) => (
                  <ListItem
                    key={unit.unitDetail.hoId}
                    title={unit.unitName}
                    onPress={() => handleUnitClick(unit)}
                    cursor="pointer"
                    style={{
                      marginTop: 12,
                      marginBottom: 12,
                    }}
                  />
                ))}
              </div>
            </Dialog>
          ) : (
            <BottomSheet        
              visible={isBottomSheetVisible}
              onClose={(from) => {
                if (from === 'closebutton') {
                  zams.z154({
                    ...getTourDanjiZamsParams(),
                    zed_dong: selectedDong?.object.dongName ?? '',
                  }).send();
                }
                handleCloseBottomSheet();
              }}
              overlayColorType="transparent"
              title={`${selectedDong?.object.dongName} 세대 확인`}
              maxHeight={344}
              hasCloseButton
            >
              <BottomSheet.ScrollView>
                {units.sort((a, b) => sortUnitNames(a.unitName, b.unitName)).map((unit) => (
                  <ListItem
                    key={unit.unitDetail.hoId} 
                    title={unit.unitName}
                    onPress={() => handleUnitClick(unit)}
                    cursor="pointer"
                    style={{
                      marginTop: 12,
                      marginBottom: 12,
                    }}
                  />
                ))}
              </BottomSheet.ScrollView>
            </BottomSheet>
          )}
        </div>
      </div>
    </>
  );
}
